import {
  Box,
  ConfirmModal,
  Dropdown,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  DropdownItem,
  NotificationVariants,
  Tag,
} from '@base-app';
import { changeMemberRole } from '@modules/teams/services/api';
import { TEAMS_QUERY_KEY, TEAMS_TRANSLATION, USER_ROLES, NON_ADMIN_USER_ROLES } from '@modules/teams/constants';
import { TeamListItem } from '@modules/teams/types';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TeamsListItemDropdown from '../TeamsListItemDropdown/TeamsListItemDropdown';
import {
  StyledTableCell,
  TableCellActionsStyled,
  TableRowStyled,
  TeamsListDesktopStyled,
} from './TeamsListDesktop.styles';
import { ChangeMemberRoleProps, OnMutateChangeUserRoleResult, TeamsListDesktopProps } from './TeamsListDesktop.types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useModal } from '@components/hooks';
import { useAddNotification } from '@modules/notifications/store';
import { intlFormatDistance } from 'date-fns';
import { isUserDeprovisioned, isUserProvisioned } from '@modules/teams/utils';
import { isUserAdministrator } from '@modules/auth/utils/userRole';
import { FETCH_DELAY } from '@modules/shared/constants';
import { isSSOUser } from '@modules/core/utils';
import useOrganizationName from '@/modules/my-account/hooks/useOrganizationName';

const TeamsListDesktop: FC<TeamsListDesktopProps> = ({
  currentUser,
  data,
  onDeactivate,
  onDelete,
  onMFAReset,
  onReactivate,
  onPasswordReset,
  onUpdate,
}) => {
  const { t } = useTranslation(TEAMS_TRANSLATION);

  const [elementHovered, setElementHovered] = useState<number | null>();
  const [selectedRole, setSelectedRole] = useState<DropdownItem>();
  const [selectedMember, setSelectedMember] = useState<TeamListItem>();
  const { isShowing, toggle } = useModal();
  const queryClient = useQueryClient();
  const addNotification = useAddNotification();
  const organizationName = useOrganizationName();

  const { mutate: changeUserRoleMutate } = useMutation(
    async (props: ChangeMemberRoleProps) => await changeMemberRole(props.roleId, props.userId),
    {
      onMutate: async (member): Promise<OnMutateChangeUserRoleResult> => {
        await queryClient.cancelQueries([TEAMS_QUERY_KEY]);
        const previousUsers = queryClient.getQueryData([TEAMS_QUERY_KEY]) as TeamListItem[];
        queryClient.setQueryData([TEAMS_QUERY_KEY], (old: TeamListItem[] | undefined): TeamListItem[] | undefined => {
          return old?.map((item) => {
            if (item.id === member.userId) {
              item.profile.pp_role = member.roleId;
              return item;
            }
            return item;
          });
        });
        return { previousUsers };
      },
      onError: (_err, id, context) => {
        queryClient.setQueryData([TEAMS_QUERY_KEY, id], context?.previousUsers);
        // TODO: Notification error TBD
      },
      onSuccess: async () => {
        addNotification({
          title: t('role_successfully_updated'),
          type: NotificationVariants.SUCCESS,
          autoClose: true,
        });
      },
      onSettled: async () => {
        setTimeout(() => {
          void queryClient.invalidateQueries([TEAMS_QUERY_KEY]);
        }, FETCH_DELAY);
      },
    },
  );

  const isRowHovered = (index: number): boolean => {
    return elementHovered === index;
  };

  const getRoleName = (item: TeamListItem): string => {
    const role = USER_ROLES.find((role) => role.id === item.profile.pp_role);
    return role !== undefined ? t(`${role.label}`) : 'Unassigned';
  };

  const handleRoleSelect = (member: TeamListItem, role: DropdownItem): void => {
    toggle();
    setSelectedMember(member);
    setSelectedRole(role);
  };

  const handleRoleChange = (): void => {
    toggle();

    if (selectedRole === undefined || selectedMember === undefined) {
      return;
    }

    const props: ChangeMemberRoleProps = {
      roleId: selectedRole.id,
      userId: selectedMember.id,
    };
    changeUserRoleMutate(props);
  };

  const handleDeactivate = (userId: string): void => {
    onDeactivate(userId);
  };

  const handleDelete = (userId: string): void => {
    onDelete(userId);
  };

  const handlePasswordReset = (userId: string): void => {
    onPasswordReset(userId);
  };

  const handleReactivate = (userId: string): void => {
    onReactivate(userId);
  };

  const handleUpdate = (userId: string, user: any): void => {
    const userToEdit = { ...user, id: userId, role: { id: user.pp_role } };
    onUpdate(userToEdit);
  };

  const statusOrRole = (item: TeamListItem): ReactNode =>
    isUserDeprovisioned(item) ? <Box css={{ color: '$red' }}>{t('deactivated')}</Box> : roleOrRoleSelector(item);

  const roleOrRoleSelector = (item: TeamListItem): ReactNode =>
    currentUser !== null &&
    isUserAdministrator(currentUser) &&
    !isUserAdministrator(item) &&
    organizationName &&
    !isSSOUser(currentUser, organizationName) ? (
      <Dropdown
        id={`dropdown-role-${item.id}`}
        items={NON_ADMIN_USER_ROLES}
        onSelect={(role) => handleRoleSelect(item, role)}
        title={getRoleName(item)}
        tableVariant={true}
      />
    ) : (
      <Box
        css={{
          fontSize: '$md',
          fontWeight: '$medium',
        }}
        data-testid={`role-${item.id}`}
      >
        {getRoleName(item)}
      </Box>
    );

  const handleMFAReset = (userId: string): void => {
    onMFAReset(userId);
  };

  return (
    <TeamsListDesktopStyled css={{ paddingTop: '$6' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className="borderless"></TableCell>
            <TableCell>{t('name_table_head')}</TableCell>
            <TableCell>{t('email_table_head')}</TableCell>
            <TableCell>{t('role_table_head')}</TableCell>
            <TableCell>{t('last_seen_table_head')}</TableCell>
            <TableCell></TableCell>
            <TableCell className="borderless"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((item, index) => (
            <TableRowStyled
              key={index}
              onMouseEnter={() => setElementHovered(index)}
              onMouseLeave={() => setElementHovered(null)}
            >
              <TableCell className="borderless"></TableCell>
              <StyledTableCell title={`${item.profile.firstName} ${item.profile.lastName}`}>
                {`${item.profile.firstName} ${item.profile.lastName}`}
                {currentUser?.id === item.id && (
                  <Tag css={{ marginLeft: '$2' }}>{t('you', { ns: 'teams' }).toUpperCase()}</Tag>
                )}
              </StyledTableCell>
              <StyledTableCell title={item.profile.email}>{item.profile.email}</StyledTableCell>
              <TableCell>{statusOrRole(item)}</TableCell>
              <TableCell>
                <Box css={{ textTransform: 'capitalize' }}>
                  {item.lastLogin && !isUserProvisioned(item)
                    ? intlFormatDistance(new Date(item.lastLogin), new Date())
                    : t('awaiting_activation', { ns: 'teams' })}
                </Box>
              </TableCell>
              <TableCellActionsStyled>
                {currentUser &&
                  isRowHovered(index) &&
                  organizationName &&
                  !isSSOUser(currentUser, organizationName) && (
                    <TeamsListItemDropdown
                      user={item}
                      onDeactivate={() => handleDeactivate(item.id)}
                      onDelete={() => handleDelete(item.id)}
                      onMFAReset={() => handleMFAReset(item.id)}
                      onPasswordReset={() => handlePasswordReset(item.id)}
                      onReactivate={() => handleReactivate(item.id)}
                      onUpdate={() => handleUpdate(item.id, item.profile)}
                      data-testid="options-dropdown"
                    />
                  )}
              </TableCellActionsStyled>
              <TableCell className="borderless"></TableCell>
            </TableRowStyled>
          ))}
        </TableBody>
      </Table>
      {isShowing && (
        <ConfirmModal
          cancelAction={{
            onClick: toggle,
            text: t('update_role_cancel'),
          }}
          confirmAction={{
            onClick: handleRoleChange,
            text: t('update_role_confirmation'),
          }}
          show={isShowing}
          title={t('update_role_modal_title')}
        />
      )}
    </TeamsListDesktopStyled>
  );
};

export default TeamsListDesktop;
